import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { CARGO, POSTURE } from "../modules/teleop-commands";
import type { PostureKey, TeleopCommands } from "../modules/teleop-commands";
import { lidForBox } from "../modules/teleop-state";
import type { RobotState } from "../modules/teleop-state";

export interface UseTeleopActionsOptions {
  commands: TeleopCommands;
  robotState: RobotState;
  /** Gate for every action below (e.g. sessionReady && configReady). */
  teleopAllowed: boolean;
}

export interface UseTeleopActionsResult {
  postureCooldown: boolean;
  cargoCooldown: Record<string, boolean>;
  cargoPending: Record<string, boolean>;
  sendPosture: (key: PostureKey) => void;
  /**
   * Dynamic counterpart of `sendPosture`, for a robot-config-advertised posture
   * command whose wire type isn't one of the two fixed `PostureKey` literals (e.g. a
   * robot-specific `posture.sit`) — see teleop-state's command-catalog.ts and
   * teleop-commands' `executeCommand`. Shares the same `postureCooldown` visual and
   * the command bus's own cooldown guard, so mixing this with `sendPosture` on the
   * same robot still debounces as one shared window.
   */
  sendPostureCommand: (type: string) => void;
  sendCargo: (boxId: string, direction: "open" | "closed") => void;
  stop: () => void;
}

/**
 * Command-gating logic shared by every UI surface that drives posture/cargo/stop
 * (`DriveControls`, `ConsolePage`) — reads `robotState.robotTelemetry` to avoid
 * resending a no-op cargo command and to clear the `pending` badge once telemetry
 * confirms the lid flipped, and enforces the same cooldown visuals everywhere.
 *
 * Composes `commands` (teleop-commands, dispatch) + `robotState` (teleop-state,
 * telemetry) at the UI layer only — neither framework-free module depends on the other.
 */
export function useTeleopActions({
  commands,
  robotState,
  teleopAllowed,
}: UseTeleopActionsOptions): UseTeleopActionsResult {
  const [postureCooldown, setPostureCooldown] = useState(false);
  const [cargoCooldown, setCargoCooldown] = useState<Record<string, boolean>>({});
  const [cargoPending, setCargoPending] = useState<Record<string, boolean>>({});
  const cargoPendingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const cargoPendingTargetRef = useRef<Record<string, "open" | "closed">>({});
  const robotStatus = useStore(robotState.robotTelemetry);

  useEffect(() => {
    const timers = cargoPendingTimersRef.current;
    return () => {
      for (const id of Object.values(timers)) clearTimeout(id);
    };
  }, []);

  const sendPosture = (key: PostureKey) => {
    if (!teleopAllowed) return;
    if (!commands.posture(key)) return;
    setPostureCooldown(true);
    window.setTimeout(() => setPostureCooldown(false), POSTURE.cooldownMs);
  };

  const sendPostureCommand = (type: string) => {
    if (!teleopAllowed) return;
    if (!commands.executeCommand({ type, mode: "edge" })) return;
    setPostureCooldown(true);
    window.setTimeout(() => setPostureCooldown(false), POSTURE.cooldownMs);
  };

  const sendCargo = (boxId: string, direction: "open" | "closed") => {
    if (!teleopAllowed) return;
    const lid = lidForBox(robotStatus?.cargo, boxId);
    if (lid === direction) return;
    const ok = commands.cargo(boxId, direction === "open" ? "open" : "close");
    if (!ok) return;
    setCargoCooldown((prev) => ({ ...prev, [boxId]: true }));
    window.setTimeout(() => {
      setCargoCooldown((prev) => ({ ...prev, [boxId]: false }));
    }, CARGO.cooldownMs);
    cargoPendingTargetRef.current[boxId] = direction;
    setCargoPending((prev) => ({ ...prev, [boxId]: true }));
    if (cargoPendingTimersRef.current[boxId]) {
      clearTimeout(cargoPendingTimersRef.current[boxId]);
    }
    cargoPendingTimersRef.current[boxId] = setTimeout(() => {
      setCargoPending((prev) => ({ ...prev, [boxId]: false }));
      delete cargoPendingTimersRef.current[boxId];
      delete cargoPendingTargetRef.current[boxId];
    }, CARGO.pendingTimeoutMs);
  };

  useEffect(() => {
    const cargo = robotStatus?.cargo;
    if (!Array.isArray(cargo)) return;
    setCargoPending((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [boxId, pending] of Object.entries(prev)) {
        if (!pending) continue;
        if (lidForBox(cargo, boxId) === cargoPendingTargetRef.current[boxId]) {
          next[boxId] = false;
          changed = true;
          if (cargoPendingTimersRef.current[boxId]) {
            clearTimeout(cargoPendingTimersRef.current[boxId]);
            delete cargoPendingTimersRef.current[boxId];
          }
          delete cargoPendingTargetRef.current[boxId];
        }
      }
      return changed ? next : prev;
    });
  }, [robotStatus]);

  const stop = () => {
    if (!teleopAllowed) return;
    commands.stop();
  };

  return {
    postureCooldown,
    cargoCooldown,
    cargoPending,
    sendPosture,
    sendPostureCommand,
    sendCargo,
    stop,
  };
}
